import { BillingPlanDto } from '@shared/core/billing'
import { GetProfileResponseDto } from '@shared/core/users'
import { routeNames } from 'src/modules/core/constants/routeNames'

/**
 * Determines whether a user should be redirected based on their authentication status,
 * profile information, and the current route.
 */
export const evaluateRouteAccess = (props: {
  path: string
  isAuthenticated: boolean
  restricted: boolean
  profile: GetProfileResponseDto
  isLoading: boolean
  currentPlan?: BillingPlanDto
}) => {
  const { path, isAuthenticated, restricted, profile, isLoading, currentPlan } = props

  let redirectPath: string

  // Only check authentication for restricted routes
  if (!isAuthenticated && restricted) {
    redirectPath = routeNames.login.path
  }

  if (isAuthenticated && path === routeNames.login.path) {
    redirectPath = routeNames.dashboard.path
  }

  // Only check subscription and profile for authenticated users
  if (isAuthenticated && !isLoading && !isRouteWithoutActivePlan(path)) {
    if (!profile || !currentPlan) {
      redirectPath = null
    } else {
      // Handle booking users - redirect to booking onboarding if needed
      if (profile?.is_booking_user) {
        // If booking user is on regular configuration, redirect to booking onboarding
        if (path === routeNames.configuration.path) {
          redirectPath = routeNames.bookingOnboarding.path
        }
        // If booking user completed regular onboarding but is on booking onboarding, allow it
        // (BookingOnboardingWrapper will handle redirects based on booking onboarding status)
        if (path === routeNames.bookingOnboarding.path) {
          // Don't redirect away - let BookingOnboardingWrapper handle it
          redirectPath = null
        }
      } else {
        // Regular SMS Reminder users
        // for users who did not complete onboarding
        if (!profile?.completed_onboarding && isRouteWithCompletedOnboarding(path)) {
          redirectPath = routeNames.configuration.path
        }

        // for users who completed onboarding
        if (profile?.completed_onboarding && isOnboardingRoute(path)) {
          redirectPath = routeNames.dashboard.path
        }
      }

      // for existing beta users
      if (
        !profile?.billing_subscription_id &&
        profile?.has_benefits &&
        profile?.completed_onboarding &&
        path !== routeNames.betaUsersSubscription.path
      ) {
        redirectPath = routeNames.betaUsersSubscription.path
      }
    }
  }

  return { redirectPath, redirect: !!redirectPath }
}

export function isRouteWithoutActivePlan(path: string): boolean {
  return (
    path.includes('confirmation') ||
    path.includes('book') ||
    path.includes('booking') ||
    path === routeNames.terms.path ||
    path === routeNames.privacy.path ||
    path === routeNames.forceLogout.path
  )
}

function isRouteWithCompletedOnboarding(path: string): boolean {
  return path.includes('dashboard') || path.includes('subscription')
}

/**
 * Twin of the function of the same name in
 * `src/modules/user/requirements/resolve-requirements.ts`. This copy feeds
 * `ProtectedRoute`, which runs *before* `RequirementsGate`, so an exception
 * added to only one of them still gets the route redirected. Keep both lists in
 * step.
 */
function isOnboardingRoute(path: string): boolean {
  return (
    path.includes('onboarding') &&
    path !== routeNames.login.path &&
    path !== routeNames.bookingOnboarding.path &&
    path !== routeNames.connectedAppAuthorize.path &&
    // The handoff redeem page. Without this the router bounces it to the
    // dashboard the instant it authenticates, silently dropping `next`.
    path !== routeNames.connectedAppLogin.path &&
    path !== routeNames.syncCalendar.path &&
    path !== routeNames.forceLogout.path
  )
}
